<app-dialog
  #dialog
  [key]="'modal-api-tokens'"
  [action]="'api-tokens-management'"
  [headerTheme]="'simple'"
  [title]="'Tokens de API'"
  [maxWidth]="'800px'">
  <div class="api-tokens">
    <!-- Para quem é o token: a origem do card 3697 foi um token criado na conta errada -->
    <div class="api-tokens__owner">
      <span class="api-tokens__avatar" aria-hidden="true">{{ userInitials }}</span>
      <span class="api-tokens__owner-text">
        <strong>{{ userName }}</strong>
        @if (user?.email) {
          <span class="api-tokens__muted"> · {{ user?.email }}</span>
        }
      </span>
    </div>
    <p class="api-tokens__hint">
      Cada token dá acesso à API e ao MCP do Cognito com as permissões deste usuário.
    </p>

    <!-- Só os cabeçalhos: o app-tab-view projeta um único conteúdo para todas as abas, então o
         conteúdo de cada aba é desenhado abaixo pelo activeTab (mesmo padrão da modal de contingência). -->
    <app-tab-view
      class="api-tokens__tabs"
      [tabViewItems]="tabItems()"
      [tabIndex]="activeTab === 'list' ? 0 : 1"
      [theme]="'line'"
      (sendTabViewActiveIndex)="selectTab($event === 0 ? 'list' : 'new')" />

    <!-- Aba: tokens existentes -->
    @if (activeTab === 'list') {
      @if (isLoadingTokens()) {
        <div class="api-tokens__state">
          <app-cog-loading-spinner [size]="40" />
          <p>Carregando os tokens...</p>
        </div>
      } @else if (!apiTokens().length) {
        <div class="api-tokens__state">
          <span class="api-tokens__state-icon"><i class="pi pi-key"></i></span>
          <strong>{{ userName }} ainda não tem tokens de API</strong>
          <p>
            Gere um para conectar o Claude, um script ou um sistema ao Cognito em nome dessa conta.
          </p>
          <app-button
            [label]="'Gerar primeiro token'"
            [icon]="'pi pi-plus'"
            [fitContent]="true"
            (sendButtonClick)="selectTab('new')"></app-button>
        </div>
      } @else {
        <div class="api-tokens__table">
          <p-table [value]="apiTokens()">
            <ng-template pTemplate="header">
              <tr>
                <th>Nome</th>
                <th>Criado em</th>
                <th>Último uso</th>
                <th>Expira em</th>
                <th>Status</th>
                <th class="api-tokens__actions-col">Ações</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-token>
              <tr [class.api-tokens__row--inactive]="getTokenStatus(token).inactive">
                <td class="api-tokens__name">{{ token.name }}</td>
                <td>{{ token.createdAt | vaultDate }}</td>
                <td>
                  @if (token.lastUsedAt) {
                    {{ token.lastUsedAt | vaultDateTime }}
                  } @else {
                    <span class="api-tokens__muted">Nunca usado</span>
                  }
                </td>
                <td>
                  @if (token.expirationDate) {
                    {{ token.expirationDate | vaultDate }}
                  } @else {
                    <span class="api-tokens__muted">—</span>
                  }
                </td>
                <td>
                  <span [class]="statusClass(token)">{{ getTokenStatus(token).label }}</span>
                </td>
                <td class="api-tokens__actions-col">
                  @if (!getTokenStatus(token).inactive) {
                    <div class="api-tokens__actions">
                      <app-button
                        [icon]="'pi pi-copy'"
                        [theme]="'white'"
                        [size]="'small'"
                        [noMargin]="true"
                        [isLoading]="isCopyingToken(token.id)"
                        [title]="'Copiar token'"
                        [ariaLabel]="'Copiar o token ' + token.name"
                        (sendButtonClick)="copyTokenFromTable(token)"></app-button>
                      <app-button
                        [icon]="'pi pi-ban'"
                        [theme]="'white'"
                        [size]="'small'"
                        [noMargin]="true"
                        class="api-tokens__revoke"
                        [isLoading]="isRevokingToken(token.id)"
                        [title]="'Revogar token'"
                        [ariaLabel]="'Revogar o token ' + token.name"
                        (sendButtonClick)="revokeToken(token)"></app-button>
                    </div>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      }
    }

    <!-- Aba: novo token -->
    @if (activeTab === 'new') {
      @if (!createdToken) {
        <form class="api-tokens__form" [formGroup]="createTokenForm" (ngSubmit)="createToken()">
          <div>
            <app-input
              [label]="'Nome do token'"
              [placeholder]="'Ex.: Claude Desktop do ' + (user?.firstName || 'usuário')"
              [formControl]="nameControl"
              [required]="true">
            </app-input>
            <p class="api-tokens__help">
              Para saber depois onde ele está sendo usado. De 3 a 50 caracteres.
            </p>
          </div>

          <div>
            <span class="api-tokens__label">Validade</span>
            <div class="api-tokens__chips" role="radiogroup" aria-label="Validade do token">
              @for (preset of validityPresets; track preset.value) {
                <button
                  type="button"
                  role="radio"
                  class="api-tokens__chip"
                  [class.is-on]="validityPreset === preset.value"
                  [attr.aria-checked]="validityPreset === preset.value"
                  (click)="selectValidity(preset.value)">
                  {{ preset.label }}
                </button>
              }
            </div>
            @if (validityPreset === 'custom') {
              <div class="api-tokens__custom-date">
                <app-calendar
                  [label]="'Data de expiração'"
                  [placeholder]="'Selecione a data'"
                  [formControl]="expirationDateControl"
                  [minDate]="minDate"
                  [required]="true">
                </app-calendar>
              </div>
            } @else if (expirationDate) {
              <p class="api-tokens__help">
                Expira em <strong>{{ expirationDate | vaultDate }}</strong
                >.
              </p>
            }
          </div>

          <div class="api-tokens__notice">
            <i class="pi pi-info-circle"></i>
            <span>
              O token será criado para <strong>{{ userName }}</strong
              >, com as permissões dessa conta.
            </span>
          </div>
        </form>
      } @else {
        <div class="api-tokens__created">
          <div class="api-tokens__created-title">
            <span class="api-tokens__success-icon"><i class="pi pi-check"></i></span>
            <div>
              <strong>Token "{{ createdTokenName }}" criado para {{ userName }}</strong>
              @if (expirationDate) {
                <p class="api-tokens__muted">Expira em {{ expirationDate | vaultDate }}.</p>
              }
            </div>
          </div>
          <span class="api-tokens__label">Token</span>
          <div class="api-tokens__token-value">
            <code>{{ createdToken }}</code>
            <app-button
              [label]="'Copiar'"
              [icon]="'pi pi-copy'"
              [fitContent]="true"
              (sendButtonClick)="copyCreatedToken()"></app-button>
          </div>
          <p class="api-tokens__help">
            Guarde em local seguro. Dá para copiar de novo pela lista de tokens enquanto ele estiver
            ativo.
          </p>
        </div>
      }
    }
  </div>

  <ng-template #footer>
    <div class="api-tokens__footer">
      @if (activeTab === 'new' && !createdToken) {
        <app-button
          [label]="'Cancelar'"
          [theme]="'white'"
          [fitContent]="true"
          (sendButtonClick)="selectTab('list')"></app-button>
        <app-button
          [label]="'Gerar token'"
          [icon]="'pi pi-key'"
          [fitContent]="true"
          [isLoading]="isCreatingToken"
          [disabled]="!isFormValid()"
          (sendButtonClick)="createToken()"></app-button>
      } @else if (activeTab === 'new' && createdToken) {
        <app-button
          [label]="'Gerar outro'"
          [theme]="'white'"
          [fitContent]="true"
          (sendButtonClick)="resetCreateTokenForm()"></app-button>
        <app-button
          [label]="'Ver tokens'"
          [fitContent]="true"
          (sendButtonClick)="selectTab('list')"></app-button>
      } @else {
        <app-button
          [label]="'Fechar'"
          [theme]="'white'"
          [fitContent]="true"
          (sendButtonClick)="onClose()"></app-button>
      }
    </div>
  </ng-template>
</app-dialog>
